import { useEffect, useState } from 'react';
import { Button, ErrorState, LoadingState } from '@assist/ui';
import { endpoints, ApiError, type PrivacyNoticeEntry } from '../state/api.js';
import { useSession } from '../state/session.js';

/**
 * Layar Pemberitahuan Privasi — M01 · rute `/privacy-notice` · izin PUBLIC.
 *
 * DUA HAL YANG MEMBEDAKANNYA DARI LAYAR LAIN DI KONSOL
 * ---------------------------------------------------
 * 1. Ia dapat dibuka TANPA sesi. Orang berada di layar masuk sebelum punya sesi,
 *    dan sebuah pemberitahuan yang hanya dapat dibaca setelah masuk adalah
 *    pemberitahuan yang terlambat (UU 27/2022 Pasal 21).
 * 2. Isinya BUKAN dari katalog i18n. Teks pemberitahuan privasi adalah data
 *    berversi milik pengendali, bukan string antarmuka: ia berubah tanpa
 *    penggelaran, dan catatan persetujuan menunjuk VERSINYA. Yang datang dari
 *    katalog hanyalah kerangka di sekelilingnya — judul, label versi, dan tombol
 *    pengalih bahasa.
 *
 * Kedua bahasa selalu dimuat sekaligus, dan orangnya dapat berpindah tanpa memuat
 * ulang apa pun: seseorang yang sedang memutuskan apakah akan menyetujui berhak
 * membaca teks itu dalam bahasa yang benar-benar ia pahami.
 */
export function PrivacyNotice({ tenantSlug }: { tenantSlug?: string | null }) {
  /**
   * SATU sumber bahasa — UAT U2-06, kelasnya.
   *
   * Layar ini pernah menyimpan `const [shown, setShown] = useState(locale)`, salinan
   * kedua bahasa yang hanya mengendalikan naskahnya. Pada layar terima undangan bentuk
   * yang sama menghasilkan layar yang separuh berpindah bahasa; di sini akibatnya lebih
   * halus (hampir seluruh layar ini adalah naskahnya) dan sebabnya sama. Yang dipakai
   * sekarang adalah bahasa SESI, dan tombol pengalih menggantinya.
   *
   * Konsekuensi yang disengaja: seseorang yang membaca pemberitahuan ini dalam bahasa
   * lain mengubah bahasa profilnya sekaligus. Itu justru yang diinginkan — orang yang
   * membaca naskah hukum dalam bahasa Indonesia ingin seluruh produknya berbahasa
   * Indonesia.
   */
  const { t, locale, setLocale } = useSession();
  const [notices, setNotices] = useState<PrivacyNoticeEntry[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    endpoints
      .privacyNotice(tenantSlug ?? null)
      .then((res) => {
        if (!alive) return;
        setNotices(res.notices);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setFailed(err instanceof ApiError ? err.problem.code : 'INTERNAL');
      });
    return () => {
      alive = false;
    };
  }, [tenantSlug]);

  if (failed) {
    return (
      <ErrorState
        title={t('privacy.notice.unavailable')}
        body={t('state.error.body')}
        code={failed}
        retryLabel={t('app.retry')}
        onRetry={() => window.location.reload()}
      />
    );
  }
  if (!notices) return <LoadingState rows={6} label={t('app.loading')} />;

  const current = notices.find((n) => n.locale === locale) ?? notices[0];
  if (!current) {
    return (
      <ErrorState
        title={t('privacy.notice.unavailable')}
        body={t('state.error.body')}
        retryLabel={t('app.retry')}
        onRetry={() => window.location.reload()}
      />
    );
  }
  const other = notices.find((n) => n.locale !== current.locale);

  return (
    <article className="assist-privacy">
      <h1>{current.title}</h1>
      <p className="assist-privacy__meta">
        {t('privacy.notice.version', { version: current.version })}
        {' · '}
        {t('privacy.notice.effectiveFrom', { date: formatDate(current.effective_from, locale) })}
      </p>
      {/*
        Kalimat Pengendali dipakai sebagai KALIMAT (UAT U2-04). `controller_name` NULL
        untuk naskah bawaan PQM, dan menyisipkan NULL ke `{publisher}` akan menghasilkan
        kalimat tanpa subjek — bentuk yang sama dengan `{ organisation: '' }` yang UAT
        temukan di /settings/privacy.
      */}
      <p className="assist-privacy__meta">{current.controller_statement}</p>
      {current.controller_name ? (
        <p className="assist-privacy__meta">
          {t('privacy.notice.publishedBy', { publisher: current.controller_name })}
        </p>
      ) : null}
      {other ? (
        <p>
          <Button
            variant="text"
            onClick={() => void setLocale(other.locale === 'en-US' ? 'en-US' : 'id-ID')}
          >
            {t('privacy.notice.otherLanguage')}
          </Button>
        </p>
      ) : null}
      {/*
        `white-space: pre-wrap` di `.assist-privacy__body`, bukan penguraian
        Markdown. Teksnya datang dari basis data dan boleh ditulis manajer tenant;
        merendernya sebagai penanda apa pun akan membuka jalur injeksi pada teks
        yang dibaca setiap pekerja. Yang dipertahankan adalah paragraf dan barisnya.
      */}
      <div className="assist-privacy__body">{current.body}</div>
      <p className="assist-privacy__meta">{current.contact}</p>
    </article>
  );
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}
